import { List, ListItem, ListItemButton, Skeleton, Typography } from '@mui/material';
import { Box } from '@mui/system';
import DOMPurify from 'dompurify';
import { DateTime } from 'luxon';
import { BenefitDTOBenefitEnum, BikeleasingNewsDTO } from 'probonio-shared-ui/api';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BenefitPaper } from '../../component/benefitTabs/BenefitPaper';
import { getBenefitIcon } from '../../module/benefits/BenefitIcon';
import BenefitSubPage from '../../module/benefits/BenefitSubPage';
import { useBikeleasingNews } from '../../module/benefits/bikeleasingV2/useBikeleasingNews';
import { useBikeleasingPrecondition } from '../../module/benefits/bikeleasingV2/useBikeleasingPrecondition';
import { AuthorizedLayout } from '../../module/navigation/AuthorizedLayout';

const NewsListItem = ({ newsItem }: { newsItem: BikeleasingNewsDTO }) => {
  return (
    <ListItem disablePadding sx={{ py: 2 }}>
      <ListItemButton
        component={Link}
        to={`/benefits/${BenefitDTOBenefitEnum.BikeleasingV2.toLowerCase()}/articles/${newsItem.uid}`}
        sx={{
          alignItems: 'stretch',
          gap: 2,
          color: 'inherit',
          minHeight: 120,
          p: 1,
          borderRadius: 1,
        }}
      >
        {newsItem.image && (
          <Box
            sx={{
              width: 200,
              borderRadius: 1,
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={newsItem.image.url}
              alt={newsItem.image.alternative}
              sx={{
                width: '100%',
                height: '100%',
                maxHeight: 200,
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </Box>
        )}

        <Box
          sx={{
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          {newsItem.published?.date && (
            <Typography>{DateTime.fromISO(newsItem.published.date).toLocaleString(DateTime.DATE_SHORT)}</Typography>
          )}

          <Typography variant="subtitle1">{newsItem.title}</Typography>

          {newsItem.bodytext && (
            <Typography
              component="span"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(newsItem.bodytext),
              }}
            />
          )}
        </Box>
      </ListItemButton>
    </ListItem>
  );
};

const BikeleasingNewsListPage: React.FC = () => {
  const { t } = useTranslation('extensions');
  const { PreconditionComponent, isPreconditionMet } = useBikeleasingPrecondition();
  const { data: newsList, isLoading } = useBikeleasingNews();
  const benefit = BenefitDTOBenefitEnum.BikeleasingV2;

  return (
    <AuthorizedLayout
      title={t('bikeleasing_v2.information.news.title')}
      breadcrumbs={[{ title: t(`benefitsModule:${benefit}.title`), to: `/benefits/${benefit.toLowerCase()}` }]}
    >
      <BenefitSubPage
        icon={getBenefitIcon(BenefitDTOBenefitEnum.BikeleasingV2)}
        title={t('bikeleasing_v2.title')}
        subTitle={t('bikeleasing_v2.information.news.title')}
      >
        {isPreconditionMet ? (
          <BenefitPaper>
            {newsList?.results && newsList.results.length > 0 && !isLoading && (
              <List
                disablePadding
                sx={{
                  '& > li:not(:last-child)': {
                    borderBottom: theme => `1px solid ${theme.palette.divider}`,
                  },
                }}
              >
                {newsList.results.map(newsItem => (
                  <NewsListItem newsItem={newsItem} key={newsItem.uid} />
                ))}
              </List>
            )}

            {!isLoading && (!newsList || newsList.results.length === 0) && (
              <Typography color="text.secondary" sx={{ mx: 'auto', my: 2, textAlign: 'center' }}>
                {t('bikeleasing_v2.information.news.noData.list')}
              </Typography>
            )}

            {isLoading &&
              Array.from({ length: 5 }).map((_, index) => (
                <ListItem disablePadding key={index}>
                  <ListItemButton
                    sx={{
                      gap: 2,
                    }}
                  >
                    <Skeleton variant="rectangular" width={200} height={120} sx={{ borderRadius: 1 }} />

                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width={100} />
                      <Skeleton variant="text" width="40%" />
                      <Skeleton variant="text" width="60%" />
                    </Box>
                  </ListItemButton>
                </ListItem>
              ))}
          </BenefitPaper>
        ) : (
          <BenefitPaper>{PreconditionComponent}</BenefitPaper>
        )}
      </BenefitSubPage>
    </AuthorizedLayout>
  );
};

export default BikeleasingNewsListPage;
